import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, Printer, AlertTriangle } from 'lucide-react';
import timesheetService, { type HoursKind, type HoursReport, type HoursReportDay } from '@/services/timesheet';
import organizationService from '@/services/organization';
import userService from '@/services/user';
import { formatLocalDateInTz, formatTimeInTz, getCurrentPayPeriod, getTodayInTz, useOrgTimezone } from '@/utils/timezone';
import { daysBetween, stepPayPeriod, type PayCycle } from '@/utils/payPeriod';
import {
  groupByPayPeriod, hoursReportCsv, matchPreset, presetRange, MAX_RANGE_DAYS, RANGE_PRESETS,
} from '@/utils/hoursReport';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';

interface Props {
  userId: number;
  userDisplayName: string;
}

const KIND_STYLE: Record<HoursKind, { label: string; badge: string; bar: string }> = {
  worked: { label: 'Worked', badge: 'bg-primary-50 text-primary-700', bar: 'bg-primary-500' },
  leave: { label: 'Leave', badge: 'bg-amber-50 text-amber-700', bar: 'bg-amber-400' },
  holiday: { label: 'Public holiday', badge: 'bg-sky-50 text-sky-700', bar: 'bg-sky-400' },
};

const h = (n: number) => n.toFixed(2);
const signed = (n: number) => (n > 0 ? `+${h(n)}` : h(n));

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
      <div className="text-xs font-medium text-neutral-500">{label}</div>
      <div
        className={cn(
          'text-xl font-semibold mt-1 tabular-nums',
          tone === 'good' && 'text-green-700',
          tone === 'bad' && 'text-red-700',
          !tone && 'text-neutral-900',
        )}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function dayFlags(d: HoursReportDay): string[] {
  return [
    d.holiday_name,
    d.open_entries ? `${d.open_entries} still clocked in — not counted` : null,
    d.missed_clock_entries ? `${d.missed_clock_entries} missed clock` : null,
    d.edited_entries ? `${d.edited_entries} edited` : null,
  ].filter((x): x is string => Boolean(x));
}

export default function EmployeeHoursReport({ userId, userDisplayName }: Props) {
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  // Null until somebody picks a range: the report then opens on the employee's
  // last pay period, which is not known until their cycle has loaded.
  const [picked, setRange] = useState<{ start: string; end: string } | null>(null);
  const [hideEmpty, setHideEmpty] = useState(false);

  // The employee's pay cycle: the org's cycle start day and fortnight anchor,
  // at this employee's frequency. Null (the report is then not grouped) when
  // the org's settings cannot be read.
  const { data: cycleInfo, isLoading: cycleLoading } = useQuery<(PayCycle & { fortnightly: boolean }) | null>({
    queryKey: ['hours-report-cycle', userId, tz],
    queryFn: async () => {
      try {
        const [orgRes, usersRes] = await Promise.all([
          organizationService.getCurrentOrganization(),
          userService.getUsers(true),
        ]);
        if (!orgRes.success || !orgRes.data) return null;
        const employee = usersRes.success && Array.isArray(usersRes.data)
          ? usersRes.data.find(u => u.id === userId)
          : undefined;
        // No frequency on the profile reads as fortnightly, as it does on the profile form.
        const fortnightly = employee?.pay_frequency !== 'weekly';
        const refDate = orgRes.data.fortnightly_cycle_reference_date;
        const current = getCurrentPayPeriod(
          orgRes.data.pay_cycle_start_day ?? 4,
          fortnightly ? 'fortnightly' : 'weekly',
          tz,
          refDate ? String(refDate).slice(0, 10) : null,
        );
        return { anchor: current.startDate, width: fortnightly ? 14 : 7, fortnightly };
      } catch {
        return null;
      }
    },
  });
  const cycle = cycleInfo ?? null;
  const range = picked ?? presetRange('last_period', today, cycle);

  const span = daysBetween(range.start, range.end) + 1;
  const rangeError =
    !range.start || !range.end
      ? 'Choose a start and end date.'
      : span < 1
        ? 'The end date is before the start date.'
        : span > MAX_RANGE_DAYS
          ? `A report can cover at most ${MAX_RANGE_DAYS} days.`
          : null;

  const { data: report, isLoading, error } = useQuery<HoursReport>({
    queryKey: ['hours-report', userId, range.start, range.end],
    enabled: !rangeError && !cycleLoading,
    queryFn: async () => {
      const res = await timesheetService.getHoursReport(userId, range.start, range.end);
      if (!res.success || !res.data) throw new Error('Could not load the report.');
      return res.data;
    },
  });

  const activePreset = matchPreset(range.start, range.end, today, cycle);
  const fmtTime = (iso: string) => formatTimeInTz(iso, tz);

  const periods = useMemo(
    () => (report && cycle ? groupByPayPeriod(report, cycle) : []),
    [report, cycle],
  );
  const isVisible = (d: HoursReportDay) =>
    !hideEmpty || d.entry_count > 0 || d.scheduled_hours > 0 || Boolean(d.holiday_name);

  const step = (direction: 'prev' | 'next') => {
    const next = stepPayPeriod({ startDate: range.start, endDate: range.end }, 'custom', direction);
    setRange({ start: next.startDate, end: next.endDate });
  };

  const exportCsv = () => {
    if (!report) return;
    const blob = new Blob([hoursReportCsv(report, fmtTime, cycle)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hours-${userDisplayName.replace(/[^\w-]+/g, '_')}-${range.start}-to-${range.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = report?.totals;
  const cats = report?.categories ?? [];

  const renderDay = (d: HoursReportDay) => {
    const weekend = d.weekday >= 6;
    const quiet = d.entry_count === 0 && d.scheduled_hours === 0 && !d.holiday_name;
    const flags = dayFlags(d);
    const varianceShown = d.scheduled_hours > 0 || d.total_hours > 0;
    return (
      <tr
        key={d.date}
        className={cn(
          quiet && 'text-neutral-400',
          weekend && 'bg-neutral-50/60',
          d.holiday_name && 'bg-sky-50/60',
        )}
      >
        <td className="px-3 py-2 whitespace-nowrap font-medium">
          {formatLocalDateInTz(d.date, tz, { weekday: 'short', day: 'numeric', month: 'short' })}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{d.scheduled_hours ? h(d.scheduled_hours) : '—'}</td>
        <td className="px-3 py-2 whitespace-nowrap">{d.first_in ? fmtTime(d.first_in) : '—'}</td>
        <td className="px-3 py-2 whitespace-nowrap">{d.last_out ? fmtTime(d.last_out) : '—'}</td>
        {cats.map(c => (
          <td key={c.category} className="px-3 py-2 text-right tabular-nums">
            {d.by_category[c.category] ? h(d.by_category[c.category]) : ''}
          </td>
        ))}
        <td className="px-3 py-2 text-right tabular-nums font-semibold">{d.total_hours ? h(d.total_hours) : '—'}</td>
        <td
          className={cn(
            'px-3 py-2 text-right tabular-nums',
            varianceShown && d.variance_hours < -0.005 && 'text-red-700',
            varianceShown && d.variance_hours > 0.005 && 'text-green-700',
          )}
        >
          {varianceShown ? signed(d.variance_hours) : ''}
        </td>
        <td className="px-3 py-2 text-xs text-neutral-600 min-w-[10rem]">
          {flags.join(' · ')}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* Range. Held back until the pay cycle is known, so the dates do not
          open on one range and jump to another. */}
      <div className={cn('bg-white border border-neutral-200 rounded-xl p-4 sm:p-5 space-y-4 print:hidden', cycleLoading && 'hidden')}>
        <div className="flex flex-wrap gap-2">
          {RANGE_PRESETS.filter(p => cycle || !p.needsCycle).map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => setRange(presetRange(p.key, today, cycle))}
              aria-pressed={activePreset === p.key}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm border transition-colors',
                activePreset === p.key
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <button
            type="button"
            onClick={() => step('prev')}
            disabled={Boolean(rangeError)}
            className="p-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
            aria-label="Previous period"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <label htmlFor="hours-report-start" className="block text-xs font-medium text-neutral-600 mb-1">From</label>
            <input
              id="hours-report-start"
              type="date"
              value={range.start}
              onChange={e => setRange({ ...range, start: e.target.value })}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm max-w-[10.5rem]"
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="hours-report-end" className="block text-xs font-medium text-neutral-600 mb-1">To</label>
            <input
              id="hours-report-end"
              type="date"
              value={range.end}
              onChange={e => setRange({ ...range, end: e.target.value })}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm max-w-[10.5rem]"
            />
          </div>
          <button
            type="button"
            onClick={() => step('next')}
            disabled={Boolean(rangeError)}
            className="p-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
            aria-label="Next period"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div className="flex gap-2 sm:ml-auto">
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={!report} className="flex items-center gap-1">
              <Download className="h-3.5 w-3.5" /> CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.print()} disabled={!report} className="flex items-center gap-1">
              <Printer className="h-3.5 w-3.5" /> Print
            </Button>
          </div>
        </div>
        {rangeError && <p className="text-sm text-red-600">{rangeError}</p>}
      </div>

      {/* Print heading — the page header and tabs are not in the printout. */}
      <div className="hidden print:block">
        <h2 className="text-lg font-semibold">Hours report — {userDisplayName}</h2>
        <p className="text-sm">
          {formatLocalDateInTz(range.start, tz)} to {formatLocalDateInTz(range.end, tz)}
        </p>
      </div>

      {error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {(error as Error).message || 'Could not load the report.'}
        </div>
      ) : (isLoading || cycleLoading) && !rangeError ? (
        <div className="text-sm text-neutral-500">Loading report…</div>
      ) : report && t ? (
        <>
          {/* Headline */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile label="Total paid hours" value={h(t.total_hours)} sub={`${t.days_in_range} days in range`} />
            <Tile label="Worked" value={h(t.worked_hours)} sub={`${t.days_worked} days worked`} />
            <Tile label="Leave" value={h(t.leave_hours)} />
            <Tile label="Public holidays" value={h(t.holiday_hours)} />
            <Tile label="Rostered" value={h(t.scheduled_hours)} />
            <Tile
              label="Against roster"
              value={signed(t.variance_hours)}
              sub={t.days_worked ? `avg ${h(t.average_worked_per_day_worked)} h per day worked` : undefined}
              tone={t.variance_hours < -0.005 ? 'bad' : t.variance_hours > 0.005 ? 'good' : undefined}
            />
          </div>

          {(t.open_entries > 0 || t.missed_clock_entries > 0 || t.edited_entries > 0) && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {[
                  t.open_entries ? `${t.open_entries} clock-in${t.open_entries === 1 ? ' is' : 's are'} still open and not counted in any total` : null,
                  t.missed_clock_entries ? `${t.missed_clock_entries} ${t.missed_clock_entries === 1 ? 'entry was' : 'entries were'} declared as a missed clock` : null,
                  t.edited_entries ? `${t.edited_entries} ${t.edited_entries === 1 ? 'entry was' : 'entries were'} edited after clocking` : null,
                ].filter(Boolean).join('; ')}.
              </span>
            </div>
          )}

          {/* By type */}
          <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <h3 className="px-5 py-3 text-sm font-semibold text-neutral-800 border-b border-neutral-200">Hours by type</h3>
            {cats.length === 0 ? (
              <p className="px-5 py-4 text-sm text-neutral-500">No hours recorded in this range.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {cats.map(c => {
                  const pct = t.total_hours > 0 ? (c.hours / t.total_hours) * 100 : 0;
                  const style = KIND_STYLE[c.kind];
                  return (
                    <li key={c.category} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="text-sm font-medium text-neutral-800 break-words">{c.category}</span>
                          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', style.badge)}>{style.label}</span>
                        </div>
                        <div className="text-sm tabular-nums text-neutral-600">
                          <span className="font-semibold text-neutral-900">{h(c.hours)} h</span>
                          <span className="ml-2 text-neutral-400">
                            {c.entries} {c.entries === 1 ? 'entry' : 'entries'} · {pct.toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className={cn('h-full rounded-full', style.bar)} style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Daily */}
          <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-800">Daily summary</h3>
                {cycleInfo && (
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Grouped by {cycleInfo.fortnightly ? 'fortnightly' : 'weekly'} pay period.
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer select-none print:hidden">
                <input
                  type="checkbox"
                  checked={hideEmpty}
                  onChange={e => setHideEmpty(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                Hide days with nothing rostered or recorded
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-xs text-neutral-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Date</th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Rostered</th>
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">First in</th>
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Last out</th>
                    {cats.map(c => (
                      <th key={c.category} className="px-3 py-2 text-right font-medium whitespace-nowrap">{c.category}</th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Total</th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">+/−</th>
                    <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {cycle
                    ? periods.map(p => {
                        const unapproved = p.total_hours - p.approved_hours;
                        return (
                          <Fragment key={p.start}>
                            <tr className="bg-neutral-100">
                              <th colSpan={7 + cats.length} scope="colgroup" className="px-3 py-2 text-left text-xs font-semibold text-neutral-700">
                                Pay period {formatLocalDateInTz(p.start, tz)} – {formatLocalDateInTz(p.end, tz)}
                                {p.partial && (
                                  <span className="ml-2 font-normal text-neutral-500">part of this period is outside the report</span>
                                )}
                              </th>
                            </tr>
                            {p.days.filter(isVisible).map(renderDay)}
                            <tr className="bg-neutral-50 font-semibold text-neutral-800">
                              <td className="px-3 pt-2 pb-1 whitespace-nowrap">Pay period total</td>
                              <td className="px-3 pt-2 pb-1 text-right tabular-nums">{h(p.scheduled_hours)}</td>
                              <td className="px-3 pt-2 pb-1" />
                              <td className="px-3 pt-2 pb-1" />
                              {cats.map(c => (
                                <td key={c.category} className="px-3 pt-2 pb-1 text-right tabular-nums">
                                  {p.by_category[c.category] ? h(p.by_category[c.category]) : ''}
                                </td>
                              ))}
                              <td className="px-3 pt-2 pb-1 text-right tabular-nums">{h(p.total_hours)}</td>
                              <td
                                className={cn(
                                  'px-3 pt-2 pb-1 text-right tabular-nums',
                                  p.variance_hours < -0.005 && 'text-red-700',
                                  p.variance_hours > 0.005 && 'text-green-700',
                                )}
                              >
                                {signed(p.variance_hours)}
                              </td>
                              <td className="px-3 pt-2 pb-1" />
                            </tr>
                            {/* No top border: this line belongs to the total above it. */}
                            <tr className="bg-neutral-50 !border-t-0">
                              <td colSpan={7 + cats.length} className="px-3 pb-2 text-xs text-neutral-600">
                                <div>
                                  Worked {h(p.worked_hours)} h · Leave {h(p.leave_hours)} h · Public holidays {h(p.holiday_hours)} h
                                </div>
                                {p.added && p.total_hours > 0.005 && (
                                  <div className="mt-0.5">
                                    {p.added.length > 0 ? (
                                      <>
                                        <span className="font-medium text-neutral-700">Added to balances on approval:</span>{' '}
                                        {p.added.map(a => `${a.label} +${h(a.hours)} h`).join(' · ')}
                                      </>
                                    ) : p.approved_hours > 0.005 ? (
                                      'Approved. Nothing was added to a leave balance here.'
                                    ) : (
                                      'Nothing added to a leave balance yet.'
                                    )}
                                    {p.estimated_hours > 0.005 && (
                                      <span>
                                        {' '}· includes an estimate at today&rsquo;s rates for {h(p.estimated_hours)} h
                                        approved before additions were recorded
                                      </span>
                                    )}
                                    {unapproved > 0.005 && (
                                      <span className="text-amber-700">
                                        {' '}· {h(unapproved)} h not approved yet, so nothing is added for {p.approved_hours > 0.005 ? 'those' : 'them'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          </Fragment>
                        );
                      })
                    : (report.days ?? []).filter(isVisible).map(renderDay)}
                </tbody>
                <tfoot className="bg-neutral-50 font-semibold text-neutral-800 border-t border-neutral-200">
                  <tr>
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h(t.scheduled_hours)}</td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2" />
                    {cats.map(c => (
                      <td key={c.category} className="px-3 py-2 text-right tabular-nums">{h(c.hours)}</td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums">{h(t.total_hours)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{signed(t.variance_hours)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
